import { useCallback, useMemo } from 'react';
import type { Direction, Language } from '../Models/enums';
import { useAppSelector } from '../Redux/hooks';
import { en } from './en';
import { he, type Dictionary, type TranslationKey } from './he';

const DICTIONARIES: Record<Language, Dictionary> = { he, en };

export const DIRECTIONS: Record<Language, Direction> = { he: 'rtl', en: 'ltr' };

/**
 * Fills `{0}`, `{1}` in order. Positional rather than named because the same
 * sentence often puts its parts in a different order in the two languages, and
 * a positional placeholder lets each locale decide where each one lands.
 */
/**
 * `one || many`: a counted sentence holds both forms and the first argument
 * picks. Whole sentences rather than a swapped noun, because Hebrew reorders
 * the singular ("שיעור אחד", not "1 שיעורים") and English does not.
 */
const COUNT_FORMS = ' || ';

function countForm(template: string, count: string | number | undefined): string {
  const split = template.indexOf(COUNT_FORMS);
  if (split === -1) return template;
  return Number(count) === 1 ? template.slice(0, split) : template.slice(split + COUNT_FORMS.length);
}

export function format(template: string, args: readonly (string | number)[]): string {
  const chosen = countForm(template, args[0]);
  if (args.length === 0) return chosen;
  return chosen.replace(/\{(\d+)\}/g, (match, index: string) => {
    const value = args[Number(index)];
    return value === undefined ? match : String(value);
  });
}

export interface Translator {
  tr(key: TranslationKey, ...args: (string | number)[]): string;
  language: Language;
  direction: Direction;
}

export function useTranslation(): Translator {
  const language = useAppSelector(state => state.ui.language);
  const dictionary = DICTIONARIES[language];
  const tr = useCallback(
    (key: TranslationKey, ...args: (string | number)[]) => format(dictionary[key], args),
    [dictionary]
  );
  return useMemo(
    () => ({ tr, language, direction: DIRECTIONS[language] }),
    [tr, language]
  );
}

/** For code outside a component, such as a document title or a toast. */
export function translate(
  language: Language,
  key: TranslationKey,
  ...args: (string | number)[]
): string {
  return format(DICTIONARIES[language][key], args);
}
